/**
 * FFT / spectral primitives.
 *
 * Iterative radix-2 with precomputed twiddles and a cached bit-reversal table.
 * Used by the FIR designer, the minimum-phase transform, the partitioned
 * convolver and the analyser. Everything is Float32Array-in / Float32Array-out
 * so the same code runs on the main thread, in a Worker and inside a worklet.
 */

export class FFT {
  readonly n: number;
  private readonly levels: number;
  private readonly cosTable: Float32Array;
  private readonly sinTable: Float32Array;
  private readonly rev: Uint32Array;

  constructor(n: number) {
    if (n < 2 || (n & (n - 1)) !== 0) throw new Error(`FFT size must be a power of two, got ${n}`);
    this.n = n;
    this.levels = Math.log2(n) | 0;
    this.cosTable = new Float32Array(n / 2);
    this.sinTable = new Float32Array(n / 2);
    for (let i = 0; i < n / 2; i++) {
      this.cosTable[i] = Math.cos((2 * Math.PI * i) / n);
      this.sinTable[i] = Math.sin((2 * Math.PI * i) / n);
    }
    this.rev = new Uint32Array(n);
    for (let i = 0; i < n; i++) {
      let x = i;
      let r = 0;
      for (let j = 0; j < this.levels; j++) {
        r = (r << 1) | (x & 1);
        x >>>= 1;
      }
      this.rev[i] = r;
    }
  }

  /** In-place complex forward transform. */
  forward(re: Float32Array, im: Float32Array): void {
    this.transform(re, im, false);
  }

  /** In-place complex inverse transform, scaled by 1/N. */
  inverse(re: Float32Array, im: Float32Array): void {
    this.transform(im, re, false); // swapping re/im gives the conjugate transform
    const inv = 1 / this.n;
    for (let i = 0; i < this.n; i++) {
      re[i] *= inv;
      im[i] *= inv;
    }
  }

  private transform(re: Float32Array, im: Float32Array, _unused: boolean): void {
    const n = this.n;
    const rev = this.rev;
    for (let i = 0; i < n; i++) {
      const j = rev[i];
      if (j > i) {
        let t = re[i]; re[i] = re[j]; re[j] = t;
        t = im[i]; im[i] = im[j]; im[j] = t;
      }
    }
    for (let size = 2; size <= n; size *= 2) {
      const half = size / 2;
      const step = n / size;
      for (let i = 0; i < n; i += size) {
        for (let j = i, k = 0; j < i + half; j++, k += step) {
          const l = j + half;
          const c = this.cosTable[k];
          const s = this.sinTable[k];
          const tre = re[l] * c + im[l] * s;
          const tim = -re[l] * s + im[l] * c;
          re[l] = re[j] - tre;
          im[l] = im[j] - tim;
          re[j] += tre;
          im[j] += tim;
        }
      }
    }
  }

  /** Real-input forward transform. Returns magnitude^2 in `out` (n/2+1 bins). */
  powerSpectrum(input: Float32Array, out: Float32Array, scratchRe: Float32Array, scratchIm: Float32Array): void {
    const n = this.n;
    scratchRe.set(input.subarray(0, n));
    scratchIm.fill(0);
    this.forward(scratchRe, scratchIm);
    for (let i = 0; i <= n / 2; i++) {
      out[i] = scratchRe[i] * scratchRe[i] + scratchIm[i] * scratchIm[i];
    }
  }
}

const cache = new Map<number, FFT>();
export function getFFT(n: number): FFT {
  let f = cache.get(n);
  if (!f) {
    f = new FFT(n);
    cache.set(n, f);
  }
  return f;
}

export const nextPow2 = (x: number): number => {
  let n = 1;
  while (n < x) n *= 2;
  return n;
};

/**
 * Linear convolution of two real signals via FFT. Result length a+b-1.
 */
export function convolveReal(a: Float32Array, b: Float32Array): Float32Array {
  const len = a.length + b.length - 1;
  const n = nextPow2(len);
  const fft = getFFT(n);
  const ar = new Float32Array(n);
  const ai = new Float32Array(n);
  const br = new Float32Array(n);
  const bi = new Float32Array(n);
  ar.set(a);
  br.set(b);
  fft.forward(ar, ai);
  fft.forward(br, bi);
  for (let i = 0; i < n; i++) {
    const r = ar[i] * br[i] - ai[i] * bi[i];
    const im = ar[i] * bi[i] + ai[i] * br[i];
    ar[i] = r;
    ai[i] = im;
  }
  fft.inverse(ar, ai);
  return ar.slice(0, len);
}

/** Complex magnitude response of an FIR at `bins` evenly spaced frequencies. */
export function firMagnitude(taps: Float32Array, bins: number, sampleRate: number): {
  freq: Float32Array;
  magDb: Float32Array;
  phaseDeg: Float32Array;
  groupDelay: Float32Array;
} {
  const n = nextPow2(Math.max(taps.length * 4, bins * 2));
  const fft = getFFT(n);
  const re = new Float32Array(n);
  const im = new Float32Array(n);
  re.set(taps);
  fft.forward(re, im);

  const freq = new Float32Array(bins);
  const magDb = new Float32Array(bins);
  const phaseDeg = new Float32Array(bins);
  const groupDelay = new Float32Array(bins);

  // Log-spaced output bins from 10 Hz to Nyquist.
  const fMin = 10;
  const fMax = sampleRate / 2;
  let prevPhase = 0;
  for (let i = 0; i < bins; i++) {
    const f = fMin * Math.pow(fMax / fMin, i / (bins - 1));
    freq[i] = f;
    const binF = (f / sampleRate) * n;
    const i0 = Math.floor(binF);
    const frac = binF - i0;
    const i1 = Math.min(i0 + 1, n - 1);
    const r = re[i0] * (1 - frac) + re[i1] * frac;
    const m = im[i0] * (1 - frac) + im[i1] * frac;
    const mag = Math.hypot(r, m);
    magDb[i] = 20 * Math.log10(Math.max(1e-9, mag));
    let ph = Math.atan2(m, r);
    // unwrap
    while (ph - prevPhase > Math.PI) ph -= 2 * Math.PI;
    while (prevPhase - ph > Math.PI) ph += 2 * Math.PI;
    if (i > 0) {
      const df = f - freq[i - 1];
      groupDelay[i] = df > 0 ? (-(ph - prevPhase) / (2 * Math.PI * df)) * 1000 : 0;
    }
    prevPhase = ph;
    phaseDeg[i] = (ph * 180) / Math.PI;
  }
  if (bins > 1) groupDelay[0] = groupDelay[1];
  return { freq, magDb, phaseDeg, groupDelay };
}
